/**
 * Acceptarea unei invitații în echipă.
 *
 * Dacă utilizatorul este deja autentificat, acceptă direct. Dacă nu, îl trimitem la
 * înregistrare păstrând tokenul, ca să nu piardă invitația (fluxul complet al cerinței #25).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthLayout } from '../components/AppShell.js';
import { Button, InlineAlert, Spinner } from '../components/ui.js';
import { api } from '../lib/api.js';
import { errorMessage, useSession } from '../lib/session.js';

export function InvitePage() {
  const { token = '' } = useParams();
  const session = useSession();
  const navigate = useNavigate();
  const [state, setState] = useState<'idle' | 'working' | 'ok' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    if (session.status === 'authenticated' && state === 'idle') setState('idle');
  }, [session.status, state]);

  async function accept() {
    setState('working');
    try {
      await api.post<{ accountId: string }>(`/invites/${token}/accept`);
      await session.refresh();
      setState('ok');
      window.setTimeout(() => navigate('/app', { replace: true }), 1200);
    } catch (error) {
      setState('error');
      setMessage(errorMessage(error));
    }
  }

  async function decline() {
    setState('working');
    try {
      await api.post(`/invites/${token}/decline`);
      setDeclined(true);
      setState('ok');
    } catch (error) {
      setState('error');
      setMessage(errorMessage(error));
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm">
        <h1 className="font-display text-display-m text-ink-950">Invitație în echipă</h1>

        <div className="mt-6 flex flex-col gap-4">
          {session.status === 'loading' ? (
            <div className="flex items-center gap-3 text-ink-600">
              <Spinner size={18} />
              <span className="text-body-s">Verificăm sesiunea…</span>
            </div>
          ) : null}

          {session.status === 'anonymous' ? (
            <>
              <p className="text-body-s text-ink-600">
                Pentru a accepta invitația ai nevoie de un cont. Îți păstrăm invitația până termini
                înregistrarea — durează sub un minut.
              </p>
              <div className="flex flex-wrap gap-2">
                <Link to={`/register?invite=${token}`}>
                  <Button>Creează cont și acceptă</Button>
                </Link>
                <Link to={`/login?invite=${token}`}>
                  <Button variant="secondary">Am deja cont</Button>
                </Link>
              </div>
            </>
          ) : null}

          {session.status === 'authenticated' && state !== 'ok' ? (
            <>
              <p className="text-body-s text-ink-600">
                Ești autentificat ca <span className="font-medium text-ink-950">{session.user?.email}</span>.
                Dacă invitația îți este adresată, o poți accepta acum.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button loading={state === 'working'} onClick={() => void accept()}>
                  Acceptă invitația
                </Button>
                <Button variant="secondary" disabled={state === 'working'} onClick={() => void decline()}>
                  Refuză
                </Button>
              </div>
            </>
          ) : null}

          {state === 'ok' ? (
            <InlineAlert tone={declined ? 'info' : 'success'} title={declined ? 'Invitație refuzată' : 'Bine ai venit în echipă'}>
              {declined ? 'Am anunțat proprietarul contului.' : 'Te ducem în dashboard…'}
            </InlineAlert>
          ) : null}

          {state === 'error' ? (
            <InlineAlert tone="danger" title="Nu am putut finaliza">
              {message}
            </InlineAlert>
          ) : null}
        </div>
      </div>
    </AuthLayout>
  );
}
